// Who may see the legal pages, and where they live: everyone, unless the api config says `legalPagesPublic: false`
// (LEGAL_PAGES_PUBLIC=0 on the api); then admins only. Pure, so it is unit tested. Kept apart from content.ts so the
// landing and the sign-in screens can link the pages without loading their text.

export type LegalPage = "privacy" | "terms" | "acceptable-use" | "voice-likeness" | "copyright" | "ai-content" | "refunds";

/** Every legal page, in footer order: its path, its title and the short label for footers and narrow screens.
 *  legal.test.ts checks these match content.ts LEGAL_DOCS. None of these paths may be an app route (router.tsx). */
export const LEGAL_LINKS: ReadonlyArray<{ page: LegalPage; path: `/${LegalPage}`; label: string; short: string }> = [
  { page: "privacy", path: "/privacy", label: "Privacy Policy", short: "Privacy" },
  { page: "terms", path: "/terms", label: "Terms of Service", short: "Terms" },
  { page: "acceptable-use", path: "/acceptable-use", label: "Acceptable Use Policy", short: "Acceptable use" },
  { page: "voice-likeness", path: "/voice-likeness", label: "Voice and Likeness Policy", short: "Voice & likeness" },
  { page: "copyright", path: "/copyright", label: "Copyright and Takedown Policy", short: "Copyright" },
  { page: "ai-content", path: "/ai-content", label: "AI Content and Disclosure", short: "AI content" },
  { page: "refunds", path: "/refunds", label: "Credits and Refunds", short: "Refunds" },
];

/** The line under the credit packs (the Credits screen), so buyers see the refund policy before they pay. */
export const CHECKOUT_NOTE = { before: "Buying credits means you agree to our", link: { path: "/refunds", text: "Credits and Refunds" }, after: "terms. Failed steps are refunded automatically." } as const;

/** "/privacy" -> "privacy", "/terms/" -> "terms", "/ai-content" -> "ai-content", anything else -> null. */
export function legalPageFor(path: string): LegalPage | null {
  const p = path.replace(/\/+$/, "") || "/";
  return LEGAL_LINKS.find((l) => l.path === p)?.page ?? null;
}

/**
 * Reads the flag from whatever GET /api/config sent. Public by default: only an explicit `false` (or "0" / "false")
 * hides the pages, so a missing, malformed or not-yet-loaded config (null) still shows them, and Google's reviewers
 * or a signed-out visitor never wait on the api to read them.
 */
export function legalPagesPublic(config: unknown): boolean {
  const o = (typeof config === "object" && config !== null ? config : {}) as Record<string, unknown>;
  const v = o.legalPagesPublic;
  return !(v === false || v === "0" || v === "false");
}

export type LegalAccess = {
  /** legalPagesPublic(api config) */
  isPublic: boolean;
  /** the signed-in user's role, if any */
  role?: "admin" | "user" | null;
};

/** May this visitor see the legal pages? */
export function canSeeLegal(a: LegalAccess): boolean {
  return a.isPublic || a.role === "admin";
}

/**
 * The legal page to show for this path, or null (not a legal path, or hidden: the app then treats the path as it does
 * any other, e.g. sign-in or "not found").
 */
export function legalScreen(path: string, a: LegalAccess): LegalPage | null {
  const page = legalPageFor(path);
  return page && canSeeLegal(a) ? page : null;
}
